import React, { useCallback, useEffect, useState } from 'react';
import {
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    Paper,
    Stack,
    Switch,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import {
    Add as AddIcon,
    Delete as DeleteIcon,
    Refresh as RefreshIcon,
    NetworkCheck as NetworkCheckIcon,
} from '@mui/icons-material';
import {
    checkConnectorService,
    connectorServicesError,
    createConnectorService,
    deleteConnectorService,
    discoverConnectorService,
    listConnectorServices,
    updateConnectorService,
} from '../Utils/connectorServicesApi';

const formatDate = (value) => (value ? new Date(value).toLocaleString('fr-FR') : null);

// Dernière réponse du service : vert si la dernière vérification a réussi, rouge si elle a échoué
function ServiceState({ service }) {
    if (service.last_error) {
        return (
            <Tooltip title={service.last_error}>
                <Chip size="small" color="error" label="Ne répond pas" />
            </Tooltip>
        );
    }
    if (service.last_seen_at) {
        return (
            <Tooltip title={`Dernière réponse : ${formatDate(service.last_seen_at)}`}>
                <Chip size="small" color="success" variant="outlined" label="Répond" />
            </Tooltip>
        );
    }
    return <Chip size="small" variant="outlined" label="Jamais vérifié" />;
}

/**
 * Services connecteurs de Connector (fiche application CONNECTOR) : chaque connecteur est un service HTTP
 * séparé du backend. Le registre vit dans le backend Connector ; cet écran l'administre (ajout par adresse,
 * activation, test de connexion, suppression). Un service ajouté est désactivé tant qu'il n'est pas activé ici.
 */
export default function ConnectorServicesPanel({ accessToken }) {
    const [services, setServices] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [busyId, setBusyId] = useState(null);

    const [addOpen, setAddOpen] = useState(false);
    const [url, setUrl] = useState('');
    const [preview, setPreview] = useState(null);
    const [addError, setAddError] = useState('');
    const [adding, setAdding] = useState(false);

    const [toDelete, setToDelete] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        setError('');
        try {
            setServices(await listConnectorServices(accessToken));
        } catch (e) {
            setError(connectorServicesError(e));
        } finally {
            setLoading(false);
        }
    }, [accessToken]);

    useEffect(() => {
        if (accessToken) load();
    }, [accessToken, load]);

    const replace = (updated) => setServices(list => list.map(s => (s.connector_id === updated.connector_id ? updated : s)));

    const run = async (connectorId, task) => {
        setBusyId(connectorId);
        setError('');
        try {
            await task();
        } catch (e) {
            setError(connectorServicesError(e));
        } finally {
            setBusyId(null);
        }
    };

    const toggle = (service) => run(service.connector_id, async () => {
        replace(await updateConnectorService(accessToken, service.connector_id, { enabled: !service.enabled }));
    });

    const check = (service) => run(service.connector_id, async () => {
        const result = await checkConnectorService(accessToken, service.connector_id);
        replace(result.connector);
    });

    const confirmDelete = () => {
        const service = toDelete;
        setToDelete(null);
        return run(service.connector_id, async () => {
            await deleteConnectorService(accessToken, service.connector_id);
            setServices(list => list.filter(s => s.connector_id !== service.connector_id));
        });
    };

    const openAdd = () => {
        setUrl('');
        setPreview(null);
        setAddError('');
        setAddOpen(true);
    };

    const search = async () => {
        setAdding(true);
        setAddError('');
        setPreview(null);
        try {
            setPreview(await discoverConnectorService(accessToken, url));
        } catch (e) {
            setAddError(connectorServicesError(e));
        } finally {
            setAdding(false);
        }
    };

    const add = async () => {
        setAdding(true);
        setAddError('');
        try {
            await createConnectorService(accessToken, preview.base_url);
            setAddOpen(false);
            await load();
        } catch (e) {
            setAddError(connectorServicesError(e));
        } finally {
            setAdding(false);
        }
    };

    const alreadyRegistered = preview && services.some(s => s.connector_id === preview.manifest?.id);

    return (
        <Card sx={{ mb: 3, border: '1px solid', borderColor: 'divider', boxShadow: 'none' }}>
            <CardContent sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        Services connecteurs ({services.length})
                    </Typography>
                    <Stack direction="row" spacing={1}>
                        <Tooltip title="Recharger la liste">
                            <span>
                                <IconButton size="small" onClick={load} disabled={loading}><RefreshIcon /></IconButton>
                            </span>
                        </Tooltip>
                        <Button variant="contained" size="small" startIcon={<AddIcon />} onClick={openAdd}>
                            Ajouter un service
                        </Button>
                    </Stack>
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Chaque connecteur est un service séparé du backend. Un service ajouté reste désactivé tant que vous ne l'activez pas ;
                    désactiver ou retirer un service laisse finir les transferts en cours et refuse les nouveaux.
                </Typography>

                {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}><CircularProgress size={28} /></Box>
                ) : services.length === 0 ? (
                    <Alert severity="info">Aucun service enregistré. Ajoutez-en un avec son adresse sur le réseau interne.</Alert>
                ) : (
                    <TableContainer component={Paper} variant="outlined">
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Connecteur</TableCell>
                                    <TableCell>Adresse</TableCell>
                                    <TableCell>Version</TableCell>
                                    <TableCell>État</TableCell>
                                    <TableCell align="center">Activé</TableCell>
                                    <TableCell align="right">Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {services.map(service => (
                                    <TableRow key={service.connector_id} hover>
                                        <TableCell>
                                            <Typography variant="body2" sx={{ fontWeight: 600 }}>{service.name || service.connector_id}</Typography>
                                            <Typography variant="caption" color="text.secondary">{service.connector_id}</Typography>
                                        </TableCell>
                                        <TableCell sx={{ fontFamily: 'monospace', fontSize: 13 }}>{service.base_url}</TableCell>
                                        <TableCell>{service.version || '—'}</TableCell>
                                        <TableCell><ServiceState service={service} /></TableCell>
                                        <TableCell align="center">
                                            <Switch
                                                size="small"
                                                checked={service.enabled}
                                                disabled={busyId === service.connector_id}
                                                onChange={() => toggle(service)}
                                                inputProps={{ 'aria-label': `Activer ${service.name || service.connector_id}` }}
                                            />
                                        </TableCell>
                                        <TableCell align="right">
                                            <Tooltip title="Tester la connexion">
                                                <span>
                                                    <IconButton size="small" disabled={busyId === service.connector_id} onClick={() => check(service)}>
                                                        <NetworkCheckIcon fontSize="small" />
                                                    </IconButton>
                                                </span>
                                            </Tooltip>
                                            <Tooltip title="Retirer du registre">
                                                <span>
                                                    <IconButton size="small" color="error" disabled={busyId === service.connector_id} onClick={() => setToDelete(service)}>
                                                        <DeleteIcon fontSize="small" />
                                                    </IconButton>
                                                </span>
                                            </Tooltip>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}
            </CardContent>

            <Dialog open={addOpen} onClose={() => !adding && setAddOpen(false)} fullWidth maxWidth="sm">
                <DialogTitle>Ajouter un service connecteur</DialogTitle>
                <DialogContent>
                    <Stack spacing={2} sx={{ mt: 1 }}>
                        <TextField
                            id="connector-service-url"
                            label="Adresse du service"
                            placeholder="http://wordpress:3000"
                            helperText="Adresse sur le réseau interne. Le manifest du service s'affiche avant l'ajout."
                            value={url}
                            onChange={(e) => { setUrl(e.target.value); setPreview(null); setAddError(''); }}
                            onKeyDown={(e) => { if (e.key === 'Enter' && url.trim() && !adding) search(); }}
                            autoFocus
                            fullWidth
                        />
                        {addError && <Alert severity="error">{addError}</Alert>}
                        {preview && (
                            <Paper variant="outlined" sx={{ p: 2 }}>
                                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                                    {preview.manifest.name || preview.manifest.id}
                                    {preview.manifest.version && <Chip size="small" label={`v${preview.manifest.version}`} sx={{ ml: 1 }} />}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">Identifiant : {preview.manifest.id}</Typography>
                                {preview.manifest.aliases?.length > 0 && (
                                    <Typography variant="body2" color="text.secondary">Alias : {preview.manifest.aliases.join(', ')}</Typography>
                                )}
                                <Typography variant="body2" color="text.secondary">Adresse : {preview.base_url}</Typography>
                                {alreadyRegistered && (
                                    <Alert severity="info" sx={{ mt: 1 }}>
                                        Ce connecteur est déjà enregistré : l'ajout met à jour son adresse et son manifest, sans changer son état.
                                    </Alert>
                                )}
                            </Paper>
                        )}
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setAddOpen(false)} disabled={adding}>Annuler</Button>
                    {preview ? (
                        <Button variant="contained" onClick={add} disabled={adding}>
                            {alreadyRegistered ? 'Mettre à jour' : 'Ajouter (désactivé)'}
                        </Button>
                    ) : (
                        <Button variant="contained" onClick={search} disabled={adding || !url.trim()}>
                            {adding ? 'Recherche…' : 'Rechercher'}
                        </Button>
                    )}
                </DialogActions>
            </Dialog>

            <Dialog open={!!toDelete} onClose={() => setToDelete(null)}>
                <DialogTitle>Retirer ce service ?</DialogTitle>
                <DialogContent>
                    <Typography>
                        « {toDelete?.name || toDelete?.connector_id} » sera retiré du registre. Les transferts en cours finissent leurs appels,
                        les nouveaux transferts qui l'utilisent seront refusés. Vous pourrez le rajouter avec son adresse.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setToDelete(null)}>Annuler</Button>
                    <Button color="error" variant="contained" onClick={confirmDelete}>Retirer</Button>
                </DialogActions>
            </Dialog>
        </Card>
    );
}
